import React, { useEffect, useState } from "react";
import { api, formatTRY, formatMoney, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useViewCurrency } from "@/context/ViewCurrencyContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { FilePdf, FileCsv } from "@phosphor-icons/react";

function CurrencyList({ obj, tone = "" }) {
  const keys = Object.keys(obj || {});
  if (keys.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <div className="space-y-0.5 text-right">
      {keys.map((k) => (
        <div key={k} className={`text-sm font-semibold ${tone}`}>{formatMoney(obj[k], k)}</div>
      ))}
    </div>
  );
}

export default function Reports() {
  const { t } = useI18n();
  const { viewCurrency } = useViewCurrency();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthly, setMonthly] = useState(null);
  const [byProp, setByProp] = useState(null);
  const [byOwner, setByOwner] = useState(null);
  const [detailed, setDetailed] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/reports/monthly?year=${year}&view_currency=${viewCurrency}`),
      api.get(`/reports/by-property?year=${year}&view_currency=${viewCurrency}`),
      api.get(`/reports/by-owner?year=${year}&view_currency=${viewCurrency}`),
      api.get(`/reports/detailed?year=${year}`),
    ]).then(([m, p, o, d]) => { setMonthly(m.data); setByProp(p.data); setByOwner(o.data); setDetailed(d.data); });
  }, [year, viewCurrency]);

  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  const chartData = monthly?.months.map((m) => ({
    name: t("months")[m.month - 1],
    income: m.income, expense: m.expense,
  })) || [];

  const download = async (kind, ext = "csv") => {
    const path = ext === "pdf"
      ? `${API}/reports/export.pdf?year=${year}&kind=${kind}`
      : `${API}/reports/export.csv?year=${year}&kind=${kind}`;
    const res = await fetch(path, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}_${year}.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">{t("reports")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">{t("yearly_pl")} {year}</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28" data-testid="reports-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="csv-detailed" variant="outline" onClick={() => download("detailed", "csv")}>
            <FileCsv size={14} className="mr-2" /> {t("export_detailed_csv")}
          </Button>
          <Button data-testid="pdf-detailed" onClick={() => download("detailed", "pdf")} className="bg-slate-900 hover:bg-slate-800">
            <FilePdf size={14} className="mr-2" /> {t("export_detailed_pdf")}
          </Button>
        </div>
      </div>

      {monthly && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="label-eyebrow">{t("total_income")}</div>
            <div className="mt-3 space-y-1">
              {Object.entries(monthly.income_by_currency || {}).map(([c, v]) => (
                <div key={c} className="font-heading text-2xl font-semibold text-emerald-600">{formatMoney(v, c)}</div>
              ))}
              {Object.keys(monthly.income_by_currency || {}).length === 0 && <div className="text-slate-400 font-heading text-2xl">—</div>}
              <div className="text-xs text-slate-500 mt-1">≈ {formatMoney(monthly.view_total_income, monthly.view_currency)}</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="label-eyebrow">{t("total_expense")}</div>
            <div className="mt-3 space-y-1">
              {Object.entries(monthly.expense_by_currency || {}).map(([c, v]) => (
                <div key={c} className="font-heading text-2xl font-semibold text-red-600">{formatMoney(v, c)}</div>
              ))}
              {Object.keys(monthly.expense_by_currency || {}).length === 0 && <div className="text-slate-400 font-heading text-2xl">—</div>}
              <div className="text-xs text-slate-500 mt-1">≈ {formatMoney(monthly.view_total_expense, monthly.view_currency)}</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="label-eyebrow">{t("net")} ({monthly.view_currency})</div>
            <div className={`font-heading text-3xl font-semibold mt-3 ${monthly.view_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatMoney(monthly.view_net, monthly.view_currency)}</div>
            <div className="text-xs text-slate-500 mt-2">{t("view_currency_hint")}</div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="label-eyebrow mb-4">{t("monthly_pl")} (TRY)</div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} />
              <YAxis tick={{ fontSize: 12, fill: "#475569" }} />
              <Tooltip formatter={(v) => formatTRY(v)} />
              <Legend />
              <Bar dataKey="income" name={t("income")} fill="#10B981" />
              <Bar dataKey="expense" name={t("expense")} fill="#EF4444" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Tabs defaultValue="property" className="w-full">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="property" data-testid="tab-property">{t("by_property")}</TabsTrigger>
          <TabsTrigger value="owner" data-testid="tab-owner">{t("by_owner")}</TabsTrigger>
          <TabsTrigger value="detailed" data-testid="tab-detailed">{t("detailed_report")}</TabsTrigger>
        </TabsList>

        <TabsContent value="property">
          <div className="flex justify-end gap-2 mb-3">
            <Button data-testid="csv-by-property" variant="outline" onClick={() => download("by-property", "csv")}>
              <FileCsv size={14} className="mr-2" /> {t("export_property_csv")}
            </Button>
            <Button data-testid="pdf-by-property" onClick={() => download("by-property", "pdf")} className="bg-slate-900 hover:bg-slate-800">
              <FilePdf size={14} className="mr-2" /> {t("export_property_pdf")}
            </Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 label-eyebrow">{t("property")}</th>
                  <th className="text-left px-4 py-3 label-eyebrow">{t("owner_name")}</th>
                  <th className="text-right px-4 py-3 label-eyebrow">{t("income")}</th>
                  <th className="text-right px-4 py-3 label-eyebrow">{t("expense")}</th>
                  <th className="text-right px-4 py-3 label-eyebrow">{t("net")} ({byProp?.view_currency || "TRY"})</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(byProp?.rows || []).length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">{t("no_data")}</td></tr>}
                {(byProp?.rows || []).map((r) => (
                  <tr key={r.property_id} data-testid={`prop-report-${r.property_id}`}>
                    <td className="px-4 py-3 font-medium align-top">{r.name}</td>
                    <td className="px-4 py-3 text-slate-600 align-top">{r.owner_name || "—"}</td>
                    <td className="px-4 py-3 align-top"><CurrencyList obj={r.income_by_currency} tone="text-emerald-600" /></td>
                    <td className="px-4 py-3 align-top"><CurrencyList obj={r.expense_by_currency} tone="text-red-600" /></td>
                    <td className={`px-4 py-3 text-right align-top font-semibold ${r.view_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatMoney(r.view_net, byProp?.view_currency || "TRY")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="owner">
          <div className="flex justify-end gap-2 mb-3">
            <Button data-testid="csv-by-owner" variant="outline" onClick={() => download("by-owner", "csv")}>
              <FileCsv size={14} className="mr-2" /> {t("export_owner_csv")}
            </Button>
            <Button data-testid="pdf-by-owner" onClick={() => download("by-owner", "pdf")} className="bg-slate-900 hover:bg-slate-800">
              <FilePdf size={14} className="mr-2" /> {t("export_owner_pdf")}
            </Button>
          </div>
          <div className="space-y-4">
            {(byOwner?.rows || []).length === 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">{t("no_data")}</div>
            )}
            {(byOwner?.rows || []).map((o) => (
              <div key={o.owner_name} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`owner-${o.owner_name}`}>
                <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200 flex-wrap gap-4">
                  <div className="font-heading text-xl font-medium text-slate-900">{o.owner_name}</div>
                  <div className="flex gap-8 text-right">
                    <div>
                      <div className="label-eyebrow">{t("income")}</div>
                      <CurrencyList obj={o.income_by_currency} tone="text-emerald-600" />
                    </div>
                    <div>
                      <div className="label-eyebrow">{t("expense")}</div>
                      <CurrencyList obj={o.expense_by_currency} tone="text-red-600" />
                    </div>
                    <div>
                      <div className="label-eyebrow">{t("net")} ({byOwner?.view_currency || "TRY"})</div>
                      <div className={`font-heading text-xl font-semibold ${o.view_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatMoney(o.view_net, byOwner?.view_currency || "TRY")}</div>
                    </div>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100">
                    <tr>
                      <th className="text-left px-6 py-2 label-eyebrow">{t("property")}</th>
                      <th className="text-right px-6 py-2 label-eyebrow">{t("income")}</th>
                      <th className="text-right px-6 py-2 label-eyebrow">{t("expense")}</th>
                      <th className="text-right px-6 py-2 label-eyebrow">{t("net")} ({byOwner?.view_currency || "TRY"})</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {o.properties.map((p) => (
                      <tr key={p.property_id}>
                        <td className="px-6 py-2 align-top">{p.name}</td>
                        <td className="px-6 py-2 align-top"><CurrencyList obj={p.income_by_currency} tone="text-emerald-600" /></td>
                        <td className="px-6 py-2 align-top"><CurrencyList obj={p.expense_by_currency} tone="text-red-600" /></td>
                        <td className={`px-6 py-2 text-right align-top font-semibold ${p.view_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{formatMoney(p.view_net, byOwner?.view_currency || "TRY")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="detailed">
          <div className="space-y-6">
            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="label-eyebrow">{t("income")}</div>
                <div className="text-sm font-semibold text-emerald-600">≈ {formatTRY(detailed?.total_income || 0)}</div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("date")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("tenant")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("property")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("owner_name")}</th>
                      <th className="text-right px-4 py-3 label-eyebrow">{t("amount")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(detailed?.income_rows || []).length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">{t("no_data")}</td></tr>}
                    {(detailed?.income_rows || []).map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2">{r.date}</td>
                        <td className="px-4 py-2 font-medium">{r.tenant_name}</td>
                        <td className="px-4 py-2 text-slate-600">{r.property_name}</td>
                        <td className="px-4 py-2 text-slate-600">{r.owner_name || "—"}</td>
                        <td className="px-4 py-2 text-right font-semibold text-emerald-600">{formatMoney(r.amount, r.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="label-eyebrow">{t("expense")}</div>
                <div className="text-sm font-semibold text-red-600">≈ {formatTRY(detailed?.total_expense || 0)}</div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("date")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("category")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("property")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("owner_name")}</th>
                      <th className="text-left px-4 py-3 label-eyebrow">{t("vendor")}</th>
                      <th className="text-right px-4 py-3 label-eyebrow">{t("amount")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(detailed?.expense_rows || []).length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">{t("no_data")}</td></tr>}
                    {(detailed?.expense_rows || []).map((r, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2">{r.date}</td>
                        <td className="px-4 py-2 font-medium">{t(r.category)}</td>
                        <td className="px-4 py-2 text-slate-600">{r.property_name}</td>
                        <td className="px-4 py-2 text-slate-600">{r.owner_name || "—"}</td>
                        <td className="px-4 py-2 text-slate-600">{r.vendor || "-"}</td>
                        <td className="px-4 py-2 text-right font-semibold text-red-600">{formatMoney(r.amount, r.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
